import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { putAuth } from '@/api/ApiCalls';
import { LandingSiteFormDtoIn, LandingSiteFormFieldDtoIn } from '@/models/in/LandingSiteFormDtoIn';
import PopupDialog from './PopupDialog';
import { LANDING_SITE_FORMS_URL, MAX_DETAIL_LENGTH } from './landingSiteForms';
import styles from './LandingSiteFormsPage.module.scss';

interface EditDetailsDialogProps {
  form: LandingSiteFormDtoIn;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * The particulars of one entry, each on its own input, published as one version. Every field is
 * shown whether or not it is being changed: what goes up is the whole popup as the visitor will
 * read it, so seeing it whole while editing is the point.
 */
const EditDetailsDialog = ({ form, onClose, onSuccess }: EditDetailsDialogProps) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();

  const [values, setValues] = useState<Record<string, string>>(form.details ?? {});
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const labelOf = (field: LandingSiteFormFieldDtoIn) =>
    t(`landingSiteForms.fields.${form.key}.${field.key}`);

  const set = (key: string, value: string) => {
    setValues((current) => ({ ...current, [key]: value }));
    setError(null);
  };

  const blankField = form.fields.find((field) => (values[field.key] ?? '').trim() === '');

  const hasChanges = form.fields.some(
    (field) => (values[field.key] ?? '').trim() !== (form.details?.[field.key] ?? '')
  );

  const save = async () => {
    if (blankField) {
      setError(t('landingSiteForms.dialog.detailRequired', { name: labelOf(blankField) }));
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      await putAuth(
        `${LANDING_SITE_FORMS_URL}/${form.key}/details`,
        { Authorization: `Bearer ${currentUser?.token}` },
        {},
        { details: Object.fromEntries(form.fields.map((field) => [field.key, values[field.key].trim()])) }
      );
      onSuccess();
      onClose();
    } catch {
      setError(t('landingSiteForms.dialog.saveError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PopupDialog
      title={t('landingSiteForms.dialog.editDetailsTitle', { name: t(`landingSiteForms.items.${form.key}`) })}
      onClose={onClose}
    >
      <div className={styles.dialogBody}>
        <div className={styles.fieldGrid}>
          {form.fields.map((field) => {
            const label = labelOf(field);
            const value = values[field.key] ?? '';

            return (
              // A paragraph takes the whole row: it is edited as a block of lines, and half a row
              // would turn every one of them into three.
              <label
                key={field.key}
                className={field.kind === 'Multiline' ? styles.fieldFullRow : styles.field}
              >
                <span className={`body-sm-regular ${styles.fieldLabel}`}>{label}</span>

                {field.kind === 'Multiline' ? (
                  <textarea
                    className={styles.fieldTextarea}
                    value={value}
                    rows={3}
                    maxLength={MAX_DETAIL_LENGTH}
                    onChange={(e) => set(field.key, e.target.value)}
                  />
                ) : (
                  <Input
                    value={value}
                    dir={field.kind === 'Url' ? 'ltr' : undefined}
                    maxLength={MAX_DETAIL_LENGTH}
                    onChange={(e) => set(field.key, e.target.value)}
                  />
                )}
              </label>
            );
          })}
        </div>

        <p className={`body-sm-regular ${styles.dialogWarning}`}>{t('landingSiteForms.dialog.publishWarning')}</p>

        {error && <p className={`body-sm-regular ${styles.error}`}>{error}</p>}

        <div className={styles.dialogActions}>
          <Button variant="white" onClick={onClose} disabled={isSubmitting}>{t('cancel')}</Button>
          <Button variant="primary" onClick={save} disabled={isSubmitting || !hasChanges}>
            {isSubmitting ? t('saving') : t('landingSiteForms.dialog.publish')}
          </Button>
        </div>
      </div>
    </PopupDialog>
  );
};

export default EditDetailsDialog;
